
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { BrainCircuit, LayoutDashboard, LogOut, Trash2, RefreshCcw, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { clearCacheWithNotification, forceFullRefresh } from '@/utils/cache/index';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleClearCache = () => {
    clearCacheWithNotification();
  };

  const handleHardRefresh = () => {
    forceFullRefresh();
  };

  const navigateToDashboard = () => {
    console.log("Header: Navigating to dashboard with preserveResults");
    // Navigate to dashboard while preserving the current results
    navigate('/dashboard', { 
      state: { 
        preserveResults: true,
        fromHeader: true 
      } 
    });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-background/80 backdrop-blur-lg border-b border-blue-500/10">
      <Link to="/" className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-md bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
          <BrainCircuit className="w-6 h-6 text-white" />
        </div>
        <span className="text-xl font-medium bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">DeepCrawl</span>
      </Link>
      
      {user ? (
        <>
          <nav className="hidden md:flex gap-8">
            <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-blue-600 transition-colors">
              Home
            </Link>
            <button 
              onClick={navigateToDashboard} 
              className="text-sm font-medium text-muted-foreground hover:text-blue-600 transition-colors bg-transparent border-none cursor-pointer"
            >
              Dashboard
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-sm font-medium py-2 px-4 rounded-md border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 text-foreground transition-colors"
                >
                  <Zap className="w-4 h-4 text-blue-500" />
                  Refresh
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border border-blue-500/20 bg-card shadow-lg">
                <DropdownMenuItem onClick={handleClearCache} className="hover:bg-blue-500/5">
                  <Trash2 className="w-4 h-4 mr-2 text-blue-500" />
                  Clear All Caches
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-blue-500/10" />
                <DropdownMenuItem onClick={handleHardRefresh} className="hover:bg-blue-500/5">
                  <RefreshCcw className="w-4 h-4 mr-2 text-blue-500" />
                  Hard Refresh Page
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button 
              onClick={navigateToDashboard} 
              className="hidden sm:flex items-center gap-2 text-sm font-medium py-2 px-4 rounded-md border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 text-foreground transition-colors"
            >
              <LayoutDashboard className="w-4 h-4 text-blue-500" />
              Dashboard
            </button>
            <Button 
              onClick={handleSignOut} 
              className="text-sm font-medium py-2 px-4 rounded-md bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <ThemeToggle />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 text-sm font-medium py-2 px-4 rounded-md border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 text-foreground transition-colors"
              >
                <Zap className="w-4 h-4 text-blue-500" />
                Refresh
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border border-blue-500/20 bg-card shadow-lg">
              <DropdownMenuItem onClick={handleClearCache} className="hover:bg-blue-500/5">
                <Trash2 className="w-4 h-4 mr-2 text-blue-500" />
                Clear All Caches
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-blue-500/10" />
              <DropdownMenuItem onClick={handleHardRefresh} className="hover:bg-blue-500/5">
                <RefreshCcw className="w-4 h-4 mr-2 text-blue-500" />
                Hard Refresh Page
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Link 
            to="/login" 
            className="text-sm font-medium py-2 px-4 rounded-md bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-colors flex items-center"
          >
            Sign In
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
